import { appColors, Tier, TIERS } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { markersOfSuccess } from '@backrs_ui/grow-ui/src/components/MarkersOfSuccess/markersOfSuccess';
import { SchoolLevel } from '@backrs_ui/grow-ui/src/models/models';

import { View, Text, StyleSheet } from '@react-pdf/renderer';

const MARKERS_OF_SUCCESS_STRINGS = appStrings.modules.results;

const BulletPointItems = ({
  lineItem,
}: {
  lineItem: { title?: string; description: string }[];
}) => {
  return (
    <View style={bulletPointStyles.container}>
      {lineItem.map((bulletPoint, index) => (
        <View key={index} style={bulletPointStyles.lineItemContainer}>
          <View style={bulletPointStyles.content} />
          <View style={bulletPointStyles.bulletPointText}>
            <Text>
              {bulletPoint.title && (
                <Text style={bulletPointStyles.boldedPointText}>{bulletPoint.title} </Text>
              )}
              {/* the Markers of Success key terms can have {{ word }} so we need to regex replace */}
              {bulletPoint.description.replace(/{{|}}/g, '')}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
};

const bulletPointStyles = StyleSheet.create({
  container: {
    paddingLeft: 4,
  },
  lineItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  content: {
    width: 4,
    height: 4,
    borderRadius: 4,
    backgroundColor: '#000000',
    marginTop: 5,
    marginRight: 6,
  },
  bulletPointText: {
    fontSize: 10,
    marginBottom: 4,
  },
  boldedPointText: {
    fontWeight: 'bold',
  },
});

type MarkersOfSuccessPDFProps = Readonly<{
  tier: Tier;
  schoolLevel: SchoolLevel;
  page: 1 | 2;
}>;

export const MarkersOfSuccessPDF = ({ tier, schoolLevel, page }: MarkersOfSuccessPDFProps) => {
  if (tier === TIERS.SEMISKILLED || tier === TIERS.ENTREPRENEURSHIP) {
    // No markers of success for these tiers
    return null;
  }

  const schoolLevelKey: SchoolLevel = schoolLevel === 'high_school' ? 'high_school' : 'college';

  const items = markersOfSuccess[schoolLevelKey][tier];

  // the high school markers are less text, so we can fit more on a page
  const pageSplit = schoolLevel === 'high_school' ? 7 : 5;

  return (
    <View
      style={{
        ...styles.container,
        // if it's the second page and is college level, we don't want to set a height
        ...(schoolLevel === 'college' && page === 2 ? { paddingBottom: 16 } : { height: '100%' }),
      }}
    >
      <View style={styles.chip}>
        <Text style={styles.title}>{MARKERS_OF_SUCCESS_STRINGS.markersOfSuccessSectionTitle}</Text>
      </View>
      <View style={styles.bulletItemsContainer}>
        {Object.entries(items)
          .slice(page === 1 ? 0 : pageSplit, page === 1 ? pageSplit : undefined)
          .map(([key, value]) => (
            <View key={key} style={styles.itemContainer}>
              <Text style={styles.itemTitle}>{key}</Text>
              <BulletPointItems lineItem={value} />
            </View>
          ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: appColors.neutral.white,
    border: `1px solid ${appColors.neutral.black}`,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingTop: 16,
    marginBottom: 16,
  },
  bulletItemsContainer: {
    paddingHorizontal: 8,
  },
  title: {
    fontSize: 10,
    color: '#367FFF',
    fontWeight: 'bold',
    fontFamily: 'Sarabun',
  },
  chip: {
    backgroundColor: appColors.color.blue7,
    padding: 5,
    paddingHorizontal: 10,
    alignSelf: 'flex-start',
    borderRadius: 3,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  itemContainer: {
    marginTop: 4,
  },
  itemTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: appColors.color.blue1,
    marginBottom: 6,
    marginTop: 4,
  },
});
